// Shadow Network — Saved (bookmarks) page. Lists user's bookmarked analyses.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Bookmark } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tShadow } from "../i18n/shadow-social";
import ProGate from "../components/shadow/ProGate";
import { canUse } from "../lib/shadow-permissions";
// Runtime-first API base (same fix as the rest of Shadow Network).
import { API_BASE as API } from "../lib/api";

const SavedPage = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !canUse("guardados", user)) {
      setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/shadow/social/saved`);
        if (mounted) setItems(data.items || []);
      } catch {
        if (mounted) setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-white/65 mb-4">Acceso de operador requerido.</p>
          <Link to="/login" className="text-amber-400 underline">
            Iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="saved-page">
      <header className="border-b border-white/5 bg-black/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <Link
            to="/shadow"
            className="inline-flex items-center gap-2 text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Shadow</span>
          </Link>
          <h1 className="inline-flex items-center gap-2 text-base font-bold uppercase tracking-[0.18em]">
            <Bookmark className="h-4 w-4 text-cyan-300" />
            {tShadow("guardados", lang)}
          </h1>
          <span className="w-20" />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 md:px-8">
        {!canUse("guardados", user) ? (
          <ProGate feature="guardados">
            {/* Render an empty placeholder grid behind the lock so the user
                can see WHAT they're missing without actually loading data. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 min-h-[260px]">
              <div className="aspect-video rounded-2xl border border-white/10 bg-white/[0.03]" />
              <div className="aspect-video rounded-2xl border border-white/10 bg-white/[0.03]" />
              <div className="aspect-video rounded-2xl border border-white/10 bg-white/[0.03]" />
            </div>
          </ProGate>
        ) : loading ? (
          <p className="text-center text-white/40 py-12">…</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-16 text-center text-white/50">
            <Bookmark className="mx-auto mb-3 h-8 w-8 text-white/25" />
            {lang === "en"
              ? "You haven't saved any analysis yet."
              : lang === "sk"
              ? "Ešte si neuložil žiadnu analýzu."
              : "Aún no has guardado ningún análisis."}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((a) => (
              <Link
                key={a.id}
                to={`/shadow#analysis-${a.id}`}
                data-testid={`saved-card-${a.id}`}
                className="block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-all hover:border-amber-400/40"
              >
                {(a.image_url || a.video_url) && (
                  <div className="aspect-video w-full overflow-hidden bg-black">
                    <img
                      src={a.image_url || a.video_url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                <div className="p-4">
                  <p className="line-clamp-2 text-sm font-semibold text-white">{a.title}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-white/40">
                    {a.ticker || "—"} · {a.direction || "—"}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SavedPage;
